import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Send, Heart, Feather, Sparkles, Cloud, Star, Moon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";

const icons = [
  { Icon: Heart, color: "#FECACA", label: "serce" },
  { Icon: Feather, color: "#E0F2FE", label: "piórko" },
  { Icon: Sparkles, color: "#FDE68A", label: "iskra" },
  { Icon: Cloud, color: "#BAE6FD", label: "chmurka" },
  { Icon: Star, color: "#FDE68A", label: "gwiazdka" },
  { Icon: Moon, color: "#DBEAFE", label: "księżyc" },
];

export default function Playground() {
  const reduced = useReducedMotion();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error("Ups! Sprawdź adres e-mail ✨");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email: normalizedEmail });
      toast.success("Tonek i Duszek zapisali Cię do chmurowej poczty 💌");
      setEmail("");
    } catch {
      toast.error("Nie udało się zapisać. Spróbuj ponownie za chwilę.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="relative py-28 md:py-40 overflow-hidden"
      data-testid="playground-section"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="relative rounded-[2.5rem] md:rounded-[3rem] bg-[#84C5F4] px-8 md:px-16 py-16 md:py-24 overflow-hidden">
          {/* Background icons */}
          <div className="absolute inset-0 opacity-70 pointer-events-none">
            {icons.map(({ Icon, color, label }, i) => (
              <motion.div
                key={label}
                className="absolute grid place-items-center rounded-full"
                style={{
                  left: `${(i * 17 + 5) % 90}%`,
                  top: `${(i * 23 + 10) % 80}%`,
                  width: 56 + (i % 3) * 12,
                  height: 56 + (i % 3) * 12,
                  background: color,
                }}
                animate={reduced ? undefined : { y: [-10, 10, -10], rotate: [-6, 6, -6] }}
                transition={
                  reduced
                    ? undefined
                    : { duration: 4 + i * 0.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }
                }
              >
                <Icon size={22} className="text-[#051937]" />
              </motion.div>
            ))}
          </div>

          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="chip !bg-white/80 !text-[#051937]"
            >
              <span className="dot" /> Chmurowa poczta
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-semibold text-white text-4xl sm:text-5xl lg:text-6xl mt-6 tracking-tight leading-[1.05] drop-shadow-[0_6px_30px_rgba(11,42,85,0.35)]"
            >
              Zostaw adres — <br />
              Tonek i Duszek wyślą bajkę.
            </motion.h2>

            <p className="mt-6 text-white/90 text-lg">
              Raz w miesiącu. Bez reklam. Tylko historia, ilustracja i miejsce na Twoje pytanie do
              Tonka i Duszka.
            </p>

            <form
              onSubmit={onSubmit}
              noValidate
              className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto"
              data-testid="newsletter-form"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="twoj@adres.pl"
                aria-label="Adres e-mail"
                data-testid="newsletter-email"
                className="flex-1 rounded-full bg-white/90 backdrop-blur px-6 py-4 text-[#051937] placeholder:text-[#0b2a55]/50 font-semibold outline-none border border-white/60 focus:border-[#FDE68A] focus:bg-white"
              />
              <motion.button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit"
                whileHover={{ scale: 1.05, rotate: [-2, 2, -2, 0] }}
                whileTap={{ scale: 0.96 }}
                transition={{ duration: 0.35 }}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 font-semibold"
              >
                <Send size={18} /> {submitting ? "Wysyłanie…" : "Wyślij"}
              </motion.button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
